import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { useColors } from '../hooks/useColors';
import { API } from '../App';

const STORAGE_KEY = 'pd_newsletter_decision'; // "subscribed" | "dismissed"

/**
 * First-visit newsletter capture modal.
 * - Shows ~1.2s after first ever page-load (or once per fresh storage clear).
 * - Calls window.__pdNewsletterOpen = true while visible so other UI
 *   (CookieConsent, FloatingCopilot) can suppress themselves.
 * - Persists user's decision in localStorage to never re-show.
 */
export default function NewsletterModal() {
  const C = useColors();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let val = null;
    try { val = localStorage.getItem(STORAGE_KEY); } catch {}
    if (val) return; // user already decided

    let cancelled = false;
    const reveal = () => {
      if (cancelled) return;
      // Small grace period after page load so the modal doesn't pop up
      // on top of a still-rendering UI.
      setTimeout(() => {
        if (cancelled) return;
        setVisible(true);
        try {
          window.__pdNewsletterOpen = true;
          window.dispatchEvent(new Event('pd-newsletter-open'));
        } catch {}
      }, 1500);
    };

    if (document.readyState === 'complete') {
      reveal();
    } else {
      window.addEventListener('load', reveal, { once: true });
    }
    return () => {
      cancelled = true;
      window.removeEventListener('load', reveal);
    };
  }, []);

  const close = (decision) => {
    try { localStorage.setItem(STORAGE_KEY, decision); } catch {}
    setVisible(false);
    try {
      window.__pdNewsletterOpen = false;
      window.dispatchEvent(new Event('pd-newsletter-close'));
    } catch {}
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = (email || '').trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const source = localStorage.getItem('pd_lead_source') || 'organic';
      await axios.post(`${API}/newsletter/subscribe`, { email: trimmed, source });
      setDone(true);
      toast.success('You\'re subscribed! Check your inbox for podcast growth tips.');
      // Auto-close after success
      setTimeout(() => close('subscribed'), 1600);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Could not subscribe right now.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 animate-[fade-in_0.3s_ease-out]"
      data-testid="newsletter-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-md"
        style={{ background: 'rgba(5,5,10,0.72)' }}
        onClick={() => close('dismissed')}
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md rounded-2xl p-6 sm:p-8 border shadow-2xl animate-[pop-in_0.4s_cubic-bezier(0.34,1.56,0.64,1)]"
        style={{
          background: '#0B0B10',
          borderColor: `${C.purple}40`,
          boxShadow: `0 30px 80px ${C.purple}33, 0 0 0 1px ${C.purple}22`,
        }}
      >
        {/* Close */}
        <button
          onClick={() => close('dismissed')}
          className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full border transition-colors hover:bg-white/5"
          style={{ borderColor: C.border, color: C.muted }}
          aria-label="Close"
          data-testid="newsletter-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <h2
          id="newsletter-modal-title"
          className="text-xl sm:text-2xl font-black leading-tight mb-2 mt-2 text-center"
          style={{ color: C.text, textWrap: 'balance' }}
        >
          Get podcast growth tips, every week.
        </h2>
        <p className="text-[13px] leading-relaxed mb-6 text-center" style={{ color: C.text2 }}>
          Join thousands of podcasters getting free strategies, swap matches and exclusive
          content drops - straight to your inbox. No spam, unsubscribe anytime.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: C.muted }} />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourpodcast.com"
              className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${C.border}`,
                color: C.text,
              }}
              autoFocus
              disabled={submitting || done}
              data-testid="newsletter-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || done}
            className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            style={{
              background: C.purple,
              boxShadow: `0 8px 28px ${C.purple}55`,
            }}
            data-testid="newsletter-submit-btn"
          >
            {done ? 'Subscribed! 🎉' : submitting ? 'Subscribing…' : 'Subscribe Free'}
          </button>
        </form>

        <button
          onClick={() => close('dismissed')}
          className="w-full mt-3 text-[11px] hover:opacity-100 opacity-70 transition-opacity"
          style={{ color: C.muted }}
          data-testid="newsletter-skip-btn"
        >
          No thanks, maybe later
        </button>

        <p className="text-[10px] text-center mt-5" style={{ color: C.muted }}>
          By subscribing you agree to our{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: C.text2 }}>
            Privacy Policy
          </Link>
          .
        </p>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pop-in {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
